import { useMemo } from "react"
import { useOrganizationPermissions } from "../useOrganizationPermissions"
import type { ItemType } from "@mimber/ui-web"
import type {
  PermissionedMenuItem,
  UsePermissionedMenuItemsParams,
  UsePermissionedMenuItemsReturn,
} from "./usePermissionedMenuItems.types"

/**
 * Hook to filter menu items based on user permissions
 */
export const usePermissionedMenuItems = ({
  items,
}: UsePermissionedMenuItemsParams): UsePermissionedMenuItemsReturn => {
  // Use useOrganizationPermissions to get all aggregated permissions
  const { hasPermission, isLoading, error } = useOrganizationPermissions()

  const filteredItems = useMemo(() => {
    if (!items || items.length === 0) {
      return []
    }

    const filterItems = (menuItems: PermissionedMenuItem[]): ItemType[] => {
      return menuItems
        .filter((item): item is PermissionedMenuItem => {
          if (!item) return false

          // If item has permission requirements, check them
          if (item.resource && item.action) {
            return hasPermission(item.resource, item.action)
          }

          // If no permission requirements, include the item
          return true
        })
        .map((item): ItemType => {
          // Handle nested children recursively
          if (item.children && item.children.length > 0) {
            const filteredChildren = filterItems(item.children)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { resource, action, ...cleanItem } = item
            return {
              ...cleanItem,
              children:
                filteredChildren.length > 0 ? filteredChildren : undefined,
            } as ItemType
          }

          // Remove resource/action from the final item (clean Ant Design type)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { resource, action, ...cleanItem } = item
          return cleanItem as ItemType
        })
        .filter((item): item is ItemType => {
          // Remove items that have no children after filtering
          if (item && "children" in item && item.children !== undefined) {
            return Array.isArray(item.children)
              ? item.children.length > 0
              : true
          }
          return true
        })
    }

    return filterItems(items)
  }, [items, hasPermission])

  const strippedItems = useMemo(() => {
    // Remove leading/trailing dividers and collapse consecutive dividers
    // that appear after permission filtering removes items between them
    const isDivider = (item: ItemType) =>
      (item as { type?: string }).type === "divider"

    const trimmed = filteredItems.filter((_, i, arr) => {
      if (!isDivider(arr[i])) return true
      // Find the nearest non-divider before and after
      const hasBefore = arr.slice(0, i).some((x) => !isDivider(x))
      const hasAfter = arr.slice(i + 1).some((x) => !isDivider(x))
      return hasBefore && hasAfter
    })

    // Collapse any remaining consecutive dividers to a single one
    return trimmed.filter(
      (item, i, arr) => !isDivider(item) || !isDivider(arr[i - 1])
    )
  }, [filteredItems])

  return {
    items: strippedItems,
    isLoading,
    error: error || null,
  }
}
